import { captureAuthRevocation, revokeAuthSession, getApiOrigin } from '../shared/api-client';
import { blockPersistentItems, unblockPersistentItems, removePersistentItemConfirmed } from './storage';
import { readPendingSecureSignOut, writePendingSecureSignOut, removePendingSecureSignOut, removeSecureItemConfirmed } from './secureStorage';
export const MOBILE_SIGN_OUT_STORAGE_KEYS = [
    'vrs_user_role', 'vrs_auth_token', 'vrs_refresh_token', 'vrs_user_info',
    'vrs_operator_instructions', 'vrs_client_auth', 'vrs_interpreter_auth',
    'vrs_interpreter_earnings', 'vrs_active_call', 'vri_usage_history',
    'vri_scheduled_sessions', 'vri_preferred_calendar_provider', 'vri_scheduling_profiles',
    'vri_selected_scheduling_profile', 'vrs_shift_started_at', 'vri_pending_invite_tokens',
    'vri_pending_invite_url', 'vri_pending_invite_draft'
];
const SECURE_KEYS = new Set(['vrs_auth_token', 'vrs_refresh_token', 'vrs_interpreter_auth']);
let state = { phase: 'checking', signedOut: false, message: 'Checking sign-out recovery…' };
let recovery;
let flight;
const listeners = new Set();
export function getNativeSignOutState() { return state; }
export function beginNativeSignIn() {
    if (state.phase !== 'ready')
        return false;
    publish('ready', '');
    return true;
}
export function subscribeNativeSignOut(listener) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
function publish(phase, message, signedOut = false) {
    state = { phase, message, signedOut };
    listeners.forEach(listener => listener());
}
function parseRecovery(raw) {
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object' || !('version' in value) || value.version !== 1
        || !('origin' in value) || value.origin !== getApiOrigin()
        || !('refreshToken' in value) || !(value.refreshToken === null
        || (typeof value.refreshToken === 'string' && value.refreshToken.length > 0))
        || !('serverConfirmed' in value) || typeof value.serverConfirmed !== 'boolean') {
        throw new Error('Sign-out recovery cannot be read.');
    }
    return value;
}
function run(operation) {
    if (flight)
        return flight;
    let complete = () => undefined;
    const pending = new Promise(resolve => { complete = resolve; });
    flight = pending;
    const timer = setTimeout(() => publish('retry', 'Sign-out is still waiting for this device. Keep this screen open, or restart the app to retry.'), 20000);
    void operation().catch(() => {
        console.warn('Native sign-out remains unconfirmed.');
        publish('retry', 'Sign-out could not be confirmed. Reconnect and retry. This account remains locked on this device.');
    }).finally(() => { clearTimeout(timer); flight = undefined; complete(); });
    return pending;
}
async function finish() {
    if (!recovery)
        throw new Error('Sign-out recovery is missing.');
    const original = recovery;
    // Save/read back before deleting credentials. A crash at any later point
    // returns to this gate; the recovery copy lives only in this Keychain service.
    await writePendingSecureSignOut(JSON.stringify(original));
    const results = await Promise.allSettled([
        ...MOBILE_SIGN_OUT_STORAGE_KEYS.map(key => SECURE_KEYS.has(key)
            ? removeSecureItemConfirmed(key) : removePersistentItemConfirmed(key)),
        (async () => {
            if (original.serverConfirmed)
                return;
            const receipt = await revokeAuthSession(original);
            if (!receipt.confirmed)
                throw new Error('Server sign-out was not confirmed.');
            original.serverConfirmed = true;
        })()
    ]);
    // Persist the server receipt even if local cleanup failed. Retrying cannot
    // accidentally revoke an account signed in later: login stays gated here.
    await writePendingSecureSignOut(JSON.stringify(original));
    if (results.some(result => result.status === 'rejected'))
        throw new Error('Sign-out is incomplete.');
    await removePendingSecureSignOut();
    recovery = undefined;
    unblockPersistentItems(MOBILE_SIGN_OUT_STORAGE_KEYS);
    publish('ready', '', true);
}
/** Must complete before native auth hydration, navigation, or queue reconnect. */
export function initializeNativeSignOut(retire) {
    if (state.phase === 'ready')
        return Promise.resolve();
    return run(async () => {
        if (!recovery) {
            const raw = await readPendingSecureSignOut();
            if (raw === null) {
                publish('ready', '');
                return;
            }
            blockPersistentItems(MOBILE_SIGN_OUT_STORAGE_KEYS);
            recovery = parseRecovery(raw);
        }
        publish('signing-out', 'Finishing sign-out…');
        retire();
        await finish();
    });
}
export function startNativeSignOut(retire) {
    if (state.phase === 'checking') {
        return initializeNativeSignOut(retire).then(() => {
            if (state.phase === 'ready' && !state.signedOut)
                return startNativeSignOut(retire);
        });
    }
    if (state.phase !== 'ready')
        return initializeNativeSignOut(retire);
    recovery = { ...captureAuthRevocation(), version: 1, serverConfirmed: false };
    publish('signing-out', 'Signing out…');
    return run(async () => {
        // Feature-private drafts must fence their pending work while the old
        // account identity still exists. All of this happens before yielding.
        try {
            retire();
        }
        finally {
            blockPersistentItems(MOBILE_SIGN_OUT_STORAGE_KEYS);
        }
        await finish();
    });
}
